/**
 * DecisionCard — tarjeta interactiva para validar una decisión (Fase 51).
 *
 * Acciones: ▶ Escuchar 10s, ✅ Confirmar, ✏️ Editar, 🗑 No es decisión.
 * Estados: default (sin acción), editando (input inline), confirmada (borde
 * verde), descartada (gris, tachado). Las ediciones persisten en
 * structureDecisionEditsAtom hasta confirm en Fase 52.
 */

import { useState, useCallback } from "react";
import styled from "styled-components";
import type { Decision, StructureDoc } from "../../lib/types.ts";
import type { DecisionEdit } from "../../state/atoms.ts";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const CARD_CONFIDENCE_COLORS: Record<string, string> = {
  high: "#16a34a",
  medium: "#d97706",
  low: "#dc2626",
};

const Card = styled.div<{ $confirmed: boolean; $dismissed: boolean }>`
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid
    ${({ theme, $confirmed }) =>
      $confirmed ? theme.color.success : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  opacity: ${({ $dismissed }) => ($dismissed ? 0.5 : 1)};
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
`;

const PlayButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.accent};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 2px 6px;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.color.accentMuted};
  }
`;

const Timestamp = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  min-width: 55px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
  min-width: 0;
`;

const DecisionText = styled.p<{ $dismissed: boolean }>`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: ${({ $dismissed }) =>
    $dismissed ? "line-through" : "none"};
  line-height: 1.4;
`;

const InlineInput = styled.input`
  font-size: ${({ theme }) => theme.font.size.md};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  width: 100%;
  box-sizing: border-box;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const MetaItem = styled.span`
  white-space: nowrap;
`;

const MetaLabel = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const ConfidenceBadge = styled.span<{ $confidence: string }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ $confidence }) =>
    CARD_CONFIDENCE_COLORS[$confidence] ?? "#6b7280"};
  color: #fff;
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ $kind: "confirm" | "dismiss" }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $kind }) =>
    $kind === "confirm"
      ? theme.color.successMuted
      : theme.color.errorMuted};
  color: ${({ theme, $kind }) =>
    $kind === "confirm" ? theme.color.success : theme.color.error};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.xs};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: "danger" }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 3px 8px;
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === "danger" ? theme.color.error : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: none;
  color: ${({ theme, $variant }) =>
    $variant === "danger" ? theme.color.error : theme.color.textPrimary};
  cursor: pointer;

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === "danger"
        ? theme.color.errorMuted
        : theme.color.accentMuted};
    border-color: ${({ theme, $variant }) =>
      $variant === "danger"
        ? theme.color.error
        : theme.color.accent};
  }
`;

const ProgressBar = styled.div`
  height: 3px;
  background: ${({ theme }) => theme.color.border};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${({ theme }) => theme.space.xs};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  width: ${({ $progress }) => $progress}%;
  height: 100%;
  background: ${({ theme }) => theme.color.accent};
  transition: width 50ms linear;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DecisionCard({
  decision,
  index,
  doc,
  edit,
  playingId,
  progress,
  onPlay,
  onEdit,
}: {
  decision: Decision;
  index: number;
  doc: StructureDoc;
  edit: DecisionEdit | undefined;
  playingId: string | null;
  progress: number;
  onPlay: (id: string, timestamp: number) => void;
  onEdit: (id: string, e: DecisionEdit) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editingText, setEditingText] = useState("");

  const isPlaying = playingId === decision.id;
  const kind = edit?.kind;
  const displayText = edit?.text ?? decision.text;

  const proposer = decision.proposedById
    ? doc.activeParticipants.find((p) => p.id === decision.proposedById)?.name
    : null;
  const approver =
    decision.approvedById === "consensus"
      ? "Consenso"
      : decision.approvedById
        ? doc.activeParticipants.find((p) => p.id === decision.approvedById)
          ?.name
        : null;
  const section = doc.sections.find((s) => s.id === decision.sectionId);

  const startEdit = useCallback(() => {
    setEditingText(displayText);
    setEditing(true);
  }, [displayText]);

  const commitEdit = useCallback(() => {
    const trimmed = editingText.trim();
    if (trimmed && trimmed !== decision.text) {
      onEdit(decision.id, { ...edit, text: trimmed });
    } else if (trimmed === decision.text && edit?.text !== undefined) {
      // Volvió al original — limpia el edit de texto
      const rest = { ...edit };
      delete rest.text;
      onEdit(decision.id, rest);
    }
    setEditing(false);
  }, [editingText, decision.id, decision.text, edit, onEdit]);

  const cancelEdit = useCallback(() => {
    setEditingText(displayText);
    setEditing(false);
  }, [displayText]);

  const handleConfirm = useCallback(() => {
    if (kind === "confirm") {
      // Revertir
      const rest = { ...edit };
      delete rest.kind;
      onEdit(decision.id, rest);
    } else {
      onEdit(decision.id, { ...edit, kind: "confirm" });
    }
  }, [kind, edit, decision.id, onEdit]);

  const handleDismiss = useCallback(() => {
    if (kind === "dismiss") {
      // Revertir
      const rest = { ...edit };
      delete rest.kind;
      onEdit(decision.id, rest);
    } else {
      onEdit(decision.id, { ...edit, kind: "dismiss" });
    }
  }, [kind, edit, decision.id, onEdit]);

  const isConfirmed = kind === "confirm";
  const isDismissed = kind === "dismiss";

  return (
    <Card $confirmed={isConfirmed} $dismissed={isDismissed}>
      <TopRow>
        <PlayButton
          onClick={() => onPlay(decision.id, decision.timestamp)}
          title="Escuchar 10s desde esta decisión"
        >
          ▶
        </PlayButton>
        <Timestamp>{formatTime(decision.timestamp)}</Timestamp>
        <Content>
          {editing ? (
            <InlineInput
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitEdit();
                if (e.key === "Escape") cancelEdit();
              }}
              onBlur={commitEdit}
              autoFocus
            />
          ) : (
            <>
              <DecisionText $dismissed={isDismissed}>
                {displayText}
              </DecisionText>
              {isConfirmed && (
                <div>
                  <StatusBadge $kind="confirm">
                    ✅ Confirmada
                  </StatusBadge>
                </div>
              )}
              {isDismissed && (
                <div>
                  <StatusBadge $kind="dismiss">
                    🗑 Descartada
                  </StatusBadge>
                </div>
              )}
            </>
          )}
        </Content>
      </TopRow>

      <MetaRow>
        <MetaItem>
          <MetaLabel>#{index + 1}</MetaLabel>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Propuesta por:</MetaLabel> {proposer ?? "—"}
        </MetaItem>
        <MetaItem>
          <MetaLabel>Aprobada por:</MetaLabel> {approver ?? "—"}
        </MetaItem>
        <MetaItem>
          <MetaLabel>Sección:</MetaLabel> {section?.title ?? "—"}
        </MetaItem>
        <MetaItem>
          <ConfidenceBadge $confidence={decision.confidence}>
            {decision.confidence}
          </ConfidenceBadge>
        </MetaItem>
      </MetaRow>

      <Actions>
        {!isConfirmed && !isDismissed && (
          <>
            <ActionButton onClick={handleConfirm}>
              ✅ Confirmar
            </ActionButton>
            <ActionButton onClick={startEdit}>
              ✏️ Editar
            </ActionButton>
            <ActionButton $variant="danger" onClick={handleDismiss}>
              🗑 No es decisión
            </ActionButton>
          </>
        )}
        {(isConfirmed || isDismissed) && (
          <ActionButton
            onClick={() => {
              const rest = { ...edit };
              delete rest.kind;
              onEdit(decision.id, rest);
            }}
          >
            ↩ Revertir
          </ActionButton>
        )}
      </Actions>

      {isPlaying && (
        <ProgressBar>
          <ProgressFill $progress={progress} />
        </ProgressBar>
      )}
    </Card>
  );
}
